/**
 * IntegrationsOverview widget — headline counts for the Integrations module.
 *
 * 3 tiles: API Keys / Webhooks / Connected Apps. Each clickable.
 */

// The Connections tile routes to `/integrations/connectors` — the ROUTE is `connectors`
// while the user-facing label is "Connections". `/integrations/connections` is not a
// route; it matched nothing and the catch-all sent the admin to /dashboard.
import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Grid, Paper, Typography } from '@mui/material';
import { Key, Webhook, Cable } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ApiError, integrationsApi } from '../../../../api';
import { queryDashboard } from '../../../../api/dashboardQuery';
import { WidgetErrorState } from '../../../../components/primitives';
import { useTranslation } from 'react-i18next';
import { translateApiError } from '../../../../i18n/translateApiError';

interface Metric {
  label: string;
  count: number | null;
  icon: React.ReactNode;
  color: string;
  path: string;
}

const IntegrationsOverview: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState<Metric[]>([
    { label: t('dashboard:widgets.integrationsOverview.apiKeys'), count: null, icon: <Key />, color: '#2196F3', path: '/integrations/api' },
    { label: t('dashboard:widgets.integrationsOverview.webhooks'), count: null, icon: <Webhook />, color: '#FF9800', path: '/integrations/webhooks' },
    { label: t('dashboard:widgets.integrationsOverview.connections'), count: null, icon: <Cable />, color: '#4CAF50', path: '/integrations/connectors' },
  ]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const overview = await queryDashboard(
          'integrations-overview',
          () => integrationsApi.integrationsOverviewApi.get(),
        );
        if (cancelled) return;
        setMetrics([
          { label: t('dashboard:widgets.integrationsOverview.apiKeys'), count: overview.api_keys_count ?? 0, icon: <Key />, color: '#2196F3', path: '/integrations/api' },
          { label: t('dashboard:widgets.integrationsOverview.webhooks'), count: overview.webhooks_count ?? 0, icon: <Webhook />, color: '#FF9800', path: '/integrations/webhooks' },
          { label: t('dashboard:widgets.integrationsOverview.connections'), count: overview.connections_count ?? 0, icon: <Cable />, color: '#4CAF50', path: '/integrations/connectors' },
        ]);
      } catch (e) {
        if (!cancelled) setError(e instanceof ApiError ? translateApiError(e, t) : t('dashboard:widgets.integrationsOverview.loadError'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [t]);

  if (loading) return <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress size={24} /></Box>;
  if (error) return <WidgetErrorState error={error} />;

  return (
    <Box sx={{ height: '100%' }}>
      <Grid container spacing={1}>
        {metrics.map((m) => (
          <Grid item xs={4} key={m.label}>
            <Paper
              elevation={0}
              onClick={() => navigate(m.path)}
              sx={{
                p: 1.5, height: '100%', borderRadius: 2, bgcolor: 'background.paper',
                cursor: 'pointer', border: '1px solid transparent',
                transition: 'border-color 120ms',
                '&:hover': { borderColor: 'divider' },
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 0.5 }}>
                <Box sx={{ width: 32, height: 32, borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: m.color }}>
                  {m.icon}
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: m.color, lineHeight: 1.2 }}>
                  {m.count === null ? '—' : m.count}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', lineHeight: 1.2 }}>
                  {m.label}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default IntegrationsOverview;
